import React, { useEffect, useRef, useState } from "react";
import { LineChart, BarChart, PieChart } from "@mui/x-charts";
import { LinkTooltip } from "./LinkTooltip";

export type BaseChart = {
    title: string;
    footerText: string;
    link?: string;
    linkDescription?: string;
};

export type XYSeries = {
    label: string;
    values: number[];
};

export type XYChartData = {
    labels: string[];
    series: XYSeries[];
};

export type PieSlice = {
    id: number;
    label: string;
    value: number;
};

export type PieChartData = {
    slices: PieSlice[];
    angles?: { start: number; end: number };
};

export type ChartBlock =
    | ({ type: "line"; data: XYChartData } & BaseChart)
    | ({ type: "bar"; data: XYChartData } & BaseChart)
    | ({ type: "pie"; data: PieChartData } & BaseChart);

export const Chart: React.FC<ChartBlock> = (props) => {
    const { type, title, footerText, link, linkDescription } = props;

    const wrapperStyle: React.CSSProperties = {
        backgroundColor: "#2196f31a",
        borderRadius: 5,
        border: "1px solid grey",
    };
    const CHART_HIGHT = 200;

    const [mounted, setMounted] = useState(false);
    const chartRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setMounted(true);
                        observer.disconnect();
                    }
                });
            },
            { threshold: 0.8 }
        );
        if (chartRef.current) observer.observe(chartRef.current);
        return () => observer.disconnect();
    }, []);

    if (type === "line" && "data" in props) {
        const { labels, series } = props.data;
        const formattedSeries = series.map((s) => ({
            label: s.label,
            data: s.values,
        }));

        return (
            <div style={wrapperStyle} ref={chartRef}>
                <ChartHeader title={title} icon="fi fi-rr-chart-line-up" />
                {mounted && <LineChart xAxis={[{ scaleType: "point", data: labels }]} series={formattedSeries} height={CHART_HIGHT} />}
                <ChartFooter text={footerText} link={link} linkDescription={linkDescription} />
            </div>
        );
    }

    if (type === "bar" && "data" in props) {
        const { labels, series } = props.data;
        const formattedSeries = series.map((s) => ({
            label: s.label,
            data: s.values,
        }));

        return (
            <div style={wrapperStyle} ref={chartRef}>
                <ChartHeader title={title} icon="fi fi-br-stats" />
                {mounted && (
                    <BarChart xAxis={[{ scaleType: "band", data: labels }]} series={formattedSeries} borderRadius={5} height={CHART_HIGHT} />
                )}
                <ChartFooter text={footerText} link={link} linkDescription={linkDescription} />
            </div>
        );
    }

    if (type === "pie" && "data" in props) {
        const { slices, angles } = props.data;
        const startAngle = ((angles?.start ?? 0) * -1) + 90;
        const endAngle = ((angles?.end ?? 360) * -1) + 90;

        return (
            <div style={wrapperStyle} ref={chartRef}>
                <ChartHeader title={title} icon="fi fi-rr-chart-pie-alt" />
                {mounted && (
                    <PieChart
                        series={[
                            {
                                data: slices,
                                highlightScope: { fade: "global", highlight: "item" },
                                faded: { innerRadius: 30, additionalRadius: -30, color: "gray", paddingAngle: 2 },
                                innerRadius: 20,
                                paddingAngle: 1,
                                cornerRadius: 5,
                                startAngle,
                                endAngle,
                            },
                        ]}
                        height={CHART_HIGHT}
                    />
                )}
                <ChartFooter text={footerText} link={link} linkDescription={linkDescription} />
            </div>
        );
    }

    return null;
};

const ChartHeader = ({ title, icon }: { title: string; icon: string }) => (
    <div
        style={{
            display: "flex",
            padding: "4px 8px",
            gap: "10px",
            flexDirection: "row",
            alignItems: "center",
            borderBottom: "1px solid grey",
        }}
    >
        <p style={{ color: "var(--color-text)" }}>{title}</p>
        <i className={icon} style={{ display: "flex", color: "grey" }}></i>
    </div>
);

const ChartFooter = ({ text, link, linkDescription }: { text: string; link?: string; linkDescription?: string }) => {
    if (!link) return null;
    return (
        <div
            style={{
                display: "flex",
                padding: "4px 8px",
                gap: "10px",
                justifyContent: "end",
                flexDirection: "row",
                alignItems: "center",
                borderTop: "1px solid grey",
            }}
        >
            <span
                className="a-with-tooltip"
                style={{
                    color: "black",
                    display: "flex",
                    flexDirection: "row",
                    gap: "10px",
                    alignItems: "center",
                }}
            >
                <p>{text}</p>
                <i className="fi fi-rr-info" style={{ display: "flex", color: "grey" }}></i>
                {linkDescription && <LinkTooltip href={link} linkDescription={linkDescription} />}
            </span>
        </div>
    );
};